import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  getBookingUrl,
  trackPopupConverted,
  trackPopupDismissed,
  trackPopupShown
} from '../utils/gtag';
import { getCookie, setCookie } from '../utils/cookies';
import './Popup.css';

// ─────────────────────────────────────────────────────────────────────────────
// EDITABLE CONFIG — Nastya / Denis: change values below to tune the popup.
// ─────────────────────────────────────────────────────────────────────────────
const CONFIG = {
  popupName: 'first_visit_offer',  // analytics identifier; rename per campaign
  delaySeconds: 25,                // show after N seconds on page
  scrollPercent: 50,               // OR when scrolled this % of page (whichever fires first)
  cookieDays: 7,                   // suppress popup for N days after dismissal/conversion
  cookieName: 'cta_popup_dismissed_v1', // bump suffix when copy/offer changes to re-show to dismissed users
  headline: 'We come to your driveway.',
  subline: 'Mobile oil change in under an hour. Same-day slots — limited daily.',
  ctaText: 'Book in 60 seconds',
  footnote: 'Licensed & insured · 7-day guarantee · From $125'
};

// Routes where popup must NOT appear (already-converting funnels + utility/legal pages).
const SUPPRESSED_ROUTE_PREFIXES = [
  '/book',
  '/admin',
  '/driver',
  '/business',
  '/cancellation',
  '/terms',
  '/privacy'
];

function isSuppressedPath(pathname) {
  return SUPPRESSED_ROUTE_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(prefix + '/'));
}

function consentBannerClosed() {
  // CookieConsent component writes to localStorage on Accept/Decline.
  // We wait for it so we never stack two modals at once.
  try {
    return localStorage.getItem('cookieConsent') !== null;
  } catch (e) {
    return true; // private mode or storage disabled — proceed
  }
}

function Popup() {
  const [visible, setVisible] = useState(false);
  const shownRef = useRef(false);
  const closeBtnRef = useRef(null);
  const location = useLocation();
  const suppressed = isSuppressedPath(location.pathname);

  const dismiss = useCallback((method) => {
    setVisible(false);
    setCookie(CONFIG.cookieName, '1', CONFIG.cookieDays);
    trackPopupDismissed(CONFIG.popupName, method);
  }, []);

  const convert = useCallback(() => {
    setCookie(CONFIG.cookieName, '1', CONFIG.cookieDays);
    trackPopupConverted(CONFIG.popupName);
    setVisible(false);
  }, []);

  // Schedule timer + scroll trigger
  useEffect(() => {
    if (suppressed) return;
    if (getCookie(CONFIG.cookieName)) return;
    if (shownRef.current) return;

    let timerId = null;
    let scrollHandler = null;
    let consentPollId = null;

    const showPopup = () => {
      if (shownRef.current) return;
      shownRef.current = true;
      setVisible(true);
      trackPopupShown(CONFIG.popupName);
      cleanup();
    };

    const cleanup = () => {
      if (timerId) { clearTimeout(timerId); timerId = null; }
      if (scrollHandler) { window.removeEventListener('scroll', scrollHandler); scrollHandler = null; }
      if (consentPollId) { clearInterval(consentPollId); consentPollId = null; }
    };

    const startTriggers = () => {
      timerId = setTimeout(showPopup, CONFIG.delaySeconds * 1000);
      scrollHandler = () => {
        const scrolled = window.scrollY;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max > 0 && (scrolled / max) * 100 >= CONFIG.scrollPercent) {
          showPopup();
        }
      };
      window.addEventListener('scroll', scrollHandler, { passive: true });
    };

    if (consentBannerClosed()) {
      startTriggers();
    } else {
      consentPollId = setInterval(() => {
        if (consentBannerClosed()) {
          clearInterval(consentPollId);
          consentPollId = null;
          startTriggers();
        }
      }, 500);
    }

    return cleanup;
  }, [suppressed, location.pathname]);

  // ESC to dismiss + focus management
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss('escape');
    };
    document.addEventListener('keydown', onKey);
    if (closeBtnRef.current) closeBtnRef.current.focus();
    // Lock body scroll while modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [visible, dismiss]);

  if (!visible || suppressed) return null;

  return (
    <div
      className="cta-popup-overlay"
      onClick={() => dismiss('outside_click')}
      data-cta-location="popup"
    >
      <div
        className="cta-popup-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cta-popup-headline"
        aria-describedby="cta-popup-subline"
      >
        <button
          ref={closeBtnRef}
          type="button"
          className="cta-popup-close"
          onClick={() => dismiss('close_button')}
          aria-label="Close popup"
        >
          ×
        </button>

        <h2 id="cta-popup-headline" className="cta-popup-headline">
          {CONFIG.headline}
        </h2>
        <p id="cta-popup-subline" className="cta-popup-subline">
          {CONFIG.subline}
        </p>

        <Link
          to={getBookingUrl('popup')}
          className="cta-popup-button"
          data-event="cta_booking_click"
          data-cta-location="popup"
          onClick={convert}
        >
          {CONFIG.ctaText}
        </Link>

        <p className="cta-popup-footnote">{CONFIG.footnote}</p>
      </div>
    </div>
  );
}

export default Popup;
